import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from 'store';

import { setEditState, setSelectedObstructionId, setSelectedRoofIndex } from 'store/slices/ToolSlice';
import { isDirectAction, selectActiveAction, selectNeedCancelConfirmation, TOOL_ACTION } from 'store/selectors/toolSelector';
import ConfirmationModal from 'components/Basic/Modal/Confirmation';
import { getToolModeHandlers, ToolModeHandlers } from 'components/tool/Editor/ToolRegistry';
import { checkIfAnyDrawnFacetExists, resetAllTransformations } from 'components/tool/utils';
import { useRefs } from 'contexts/RefContext';

export default function EscapeKeyHandler() {
	const dispatch = useDispatch<AppDispatch>();

	const { designType } = useSelector((state: RootState) => state.roofData.data);
	const { editModeEnabled, selectedRoofIndex, isCancelModalOpen, selectedObstructionId, pendingAzimuthConfirmationFacet } =
		useSelector((state: RootState) => state.toolNewPostions.data);
	const currentActiveActionOnTool = useSelector(selectActiveAction);
	const showConfirmationIfAnyUnsavedChanges = useSelector(selectNeedCancelConfirmation);

	const {konvaRef} = useRefs();

	const modeHandlersRef = useRef<ToolModeHandlers>();
	const [confirmationOnEsc, setConfirmationOnEsc] = 
		useState<{ message?: string, show: boolean }>({
			message: 'Are you sure you want to discard changes?',
			show: false
		});

	useEffect(() => {
		const handleKeyUp = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') return;
			if( isCancelModalOpen ) return;

			if (!editModeEnabled) return;
			const doesAnyDrawnFacetExists = checkIfAnyDrawnFacetExists(konvaRef?.current);
			if (currentActiveActionOnTool === TOOL_ACTION.DRAW_FACET && doesAnyDrawnFacetExists) return;
			if (currentActiveActionOnTool === TOOL_ACTION.EDIT_AZIMUTH_OR_POSITION && pendingAzimuthConfirmationFacet) return;

			// 1. No active action => exit edit mode OR if in edit obstruction mode, unselect any selected obstruction
			if (!currentActiveActionOnTool || (currentActiveActionOnTool === TOOL_ACTION.EDIT_OBSTRUCTION && selectedObstructionId)) {
				if(selectedObstructionId){
					dispatch(setSelectedObstructionId({ id: null }));
					return;
				}
				if(selectedRoofIndex){
					dispatch(setSelectedRoofIndex({ selectedRoofIndex: null }));
					if (konvaRef?.current) resetAllTransformations(konvaRef.current, designType === 'ROOF');
					return;
				}
				dispatch(setEditState({ enabled: false }));
				return;
			}

			// 2. Direct actions => esc does nothing 
			// since user has to confirm the action first
			if (isDirectAction(currentActiveActionOnTool)) {
				console.log(currentActiveActionOnTool);
				return;
			}

			// 3. Action Modes
			const handlers = getToolModeHandlers(currentActiveActionOnTool);
			modeHandlersRef.current = handlers;

			// TODO : case not handled: draw facet mode => since esc has undo facet role
			// unsaved changes => show confirmation modal
			if (showConfirmationIfAnyUnsavedChanges) {
				setConfirmationOnEsc({
					show: true,
					message: handlers?.cancelConfirmMessage
				});
				return;
			}

			handlers?.cancel?.();
		};

		window.addEventListener('keyup', handleKeyUp);
		return () => window.removeEventListener('keyup', handleKeyUp);
	}, [currentActiveActionOnTool, editModeEnabled, showConfirmationIfAnyUnsavedChanges, dispatch, selectedRoofIndex,
		konvaRef, designType, isCancelModalOpen, selectedObstructionId, pendingAzimuthConfirmationFacet]);

	function handleConfirmation() {
		modeHandlersRef.current?.cancel?.();
		setConfirmationOnEsc({ show: false });
	}

	return <ConfirmationModal
		open={confirmationOnEsc.show}
		message={confirmationOnEsc.message ?? 'Are you sure you want to discard changes?'}
		onConfirm={handleConfirmation}
		onCancel={() => setConfirmationOnEsc({ show: false })}
	/>;
}
